import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDate } from "@/lib/status";
import { Bell, CheckCheck } from "lucide-react";

function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    [880, 1320].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      const t = ctx.currentTime + i * 0.18;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      o.connect(g).connect(ctx.destination);
      o.start(t);
      o.stop(t + 0.32);
    });
    setTimeout(() => ctx.close(), 800);
  } catch (e) { /* som indisponível */ }
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const lastCount = useRef(null);
  const openRef = useRef(false);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      if (lastCount.current !== null && data.count > lastCount.current) {
        playChime();
        try {
          const { data: list } = await api.get("/notifications");
          if (openRef.current) setItems(list);
          const n = list.find((x) => !x.read);
          if (n) toast(n.title, { description: n.message });
        } catch (e) { /* ignore */ }
      }
      lastCount.current = data.count;
      setUnread(data.count);
    } catch (e) {
      console.error("unread count fetch failed", e);
    }
  }, []);

  useEffect(() => {
    loadCount();
    const i = setInterval(loadCount, 15000);
    return () => clearInterval(i);
  }, [loadCount]);

  const onOpenChange = async (o) => {
    setOpen(o);
    openRef.current = o;
    if (o) {
      setLoading(true);
      try {
        const { data } = await api.get("/notifications");
        setItems(data);
        if (unread > 0) {
          await api.post("/notifications/read-all");
          setUnread(0);
          lastCount.current = 0;
        }
      } catch (e) {
        console.error("notifications fetch failed", e);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="h-5 w-5 text-slate-600" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notification-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <span className="font-semibold text-slate-800 text-sm">Notificações</span>
          <CheckCheck className="h-4 w-4 text-slate-400" />
        </div>
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <p className="text-sm text-slate-400 text-center py-8">Carregando...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">Nenhuma notificação ainda.</p>
          ) : (
            items.map((n) => (
              <div key={n.id} className="px-4 py-3 border-b border-slate-50 hover:bg-slate-50" data-testid={`notification-${n.id}`}>
                <p className="text-sm font-medium text-slate-800">{n.title}</p>
                <p className="text-sm text-slate-500">{n.message}</p>
                <p className="text-xs text-slate-400 mt-0.5">{formatDate(n.created_at)}</p>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
